import { StoresContext } from '@App/contexts'
import React, { useContext, useRef, useState } from 'react'
import DocumentUploadForm, { DocumentUploadFormButtonsOverride } from '@Components/DocumentUploadForm'
import { Maybe } from '@Types'
import { generateCatalogTransferDocumentUploadUrl } from '@App/api/routes/rightsholder/catalog-transfer'
import Button from '@Components/Buttons/Button'
import { UnifiedDocument } from '@Entities/document/document-to-upload'
import './index.scss'
import ImportCatalogStepHeader from '@Scenes/RightsholderScene/CatalogTransfer/CatalogTransferWizard/ImportCatalogStep/ImportCatalogStepHeader'
import ModalPortal from '@Components/Popups/ModalPortal'
import ImportCatalogErrorModal from '@Scenes/RightsholderScene/CatalogTransfer/CatalogTransferWizard/ImportCatalogStep/ImportCatalogErrorModal'
import { useFlag } from '@openfeature/react-sdk'
import _ from 'lodash'

const CATALOG_TRANSFER_IMPORT_ACCEPTED_FILE_TYPES = ['.xlsx']
const CATALOG_TRANSFER_IMPORT_MAX_FILE_SIZE_IN_BYTES = 25 * 1024 * 1024
const IMPORT_STREAM_FLAG_KEY = 'catalog-transfer-import-stream'

const ImportCatalogStep = () => {
    const { catalogTransferWizardStore, rightsholderStore } = useContext(StoresContext)
    const { value: importStreamFlagEnabled } = useFlag(IMPORT_STREAM_FLAG_KEY, false)
    const documentUploadFormButtonsOverrideRef = useRef<DocumentUploadFormButtonsOverride>(null)
    const [areFilesStaged, setAreFilesStaged] = useState<boolean>(false)
    const [importConfirmationVisible, setImportConfirmationVisible] = useState<boolean>(false)
    const [modalIsOpen, setModalIsOpen] = useState<boolean>(false)

    const onUpload = async () => {
        if (importStreamFlagEnabled) {
            await catalogTransferWizardStore.importWorksFromExcelStream()
        } else {
            await catalogTransferWizardStore.importWorksFromExcel()
        }

        setImportConfirmationVisible(true)
        if (!catalogTransferWizardStore.importResult?.success) {
            setModalIsOpen(true)
        }
        setAreFilesStaged(false)
    }

    const closeModalAndReset = () => {
        catalogTransferWizardStore.resetImport()
        documentUploadFormButtonsOverrideRef.current?.resetForm()
        setImportConfirmationVisible(false)
        setModalIsOpen(false)
    }

    const onFileStaged = (files: UnifiedDocument[]) => {
        const hasNewStagedFile = files.some((file) => file.isComplete !== true)

        if (importConfirmationVisible && hasNewStagedFile) {
            catalogTransferWizardStore.resetImport()
            setImportConfirmationVisible(false)
        }

        setAreFilesStaged(files.length !== 0)
    }

    const generateS3PresignedUrl = async (fileName: string, fileSize: number, fileDescription: Maybe<string>) => {
        const response = await generateCatalogTransferDocumentUploadUrl(
            rightsholderStore.currentRightsholderId,
            catalogTransferWizardStore.currentTransferId,
            'CATALOG_IMPORT',
            fileName,
            fileSize,
            fileDescription
        )
        return _.omit(response.data, 'catalogTransferMemberUploadId')
    }

    return (
        <div className="ct-export-catalog-step ct-wizard-step-container">
            <DocumentUploadForm
                title={<ImportCatalogStepHeader />}
                displayConfig={{
                    showDescription: false,
                    hideUploadButtons: true,
                    hideConfirmationText: true,
                    browseFilesButtonText: 'Browse Files to Upload',
                }}
                onUpload={onUpload}
                onFileStaged={onFileStaged}
                allowedFilesConfig={{
                    allowedFileTypes: CATALOG_TRANSFER_IMPORT_ACCEPTED_FILE_TYPES,
                    maximumFileSizeInBytes: CATALOG_TRANSFER_IMPORT_MAX_FILE_SIZE_IN_BYTES,
                }}
                generateS3PresignedUrl={generateS3PresignedUrl}
                ref={documentUploadFormButtonsOverrideRef}
                allowMultipleUploads={false}
                showDescriptionUpdate={false}
            />
            {!importConfirmationVisible && areFilesStaged && (
                <Button
                    mainColor
                    type="primary"
                    onClick={() => documentUploadFormButtonsOverrideRef.current?.uploadDocuments()}
                >
                    Upload Verified Works File
                </Button>
            )}
            {importConfirmationVisible && (
                <b className="files-uploaded-confirmation-text">
                    Your file was successfully uploaded
                    {catalogTransferWizardStore.importResult?.success ? '!' : ' but failed to import.'}
                </b>
            )}
            <ModalPortal isOpen={modalIsOpen} onClose={() => closeModalAndReset}>
                <ImportCatalogErrorModal closeAndReset={closeModalAndReset} />
            </ModalPortal>
        </div>
    )
}

export default ImportCatalogStep
